import React, { CSSProperties, useState } from 'react';
import Offcanvas, { OffcanvasPlacement } from 'react-bootstrap/Offcanvas';
import ReactDOM from 'react-dom';
import ReactDOMServer from 'react-dom/server';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import WorkspaceBotoes from '../WorkspaceBotoes';

const StyledOffcanvas = styled.div`
  display: grid;
  grid-template-rows: 7% 88% 5%; /* 5% Header, 90% Body, 5% Footer */
  height: 100vh; /* Garante que o layout ocupa toda a altura da tela */
  width: 100%;
`;

const StyledOffcanvasHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  border-bottom: 1px solid #ddd;
  background-color: ${({ theme }) => theme.colors.azulgov};
  color: white;
  font-size: 1.5rem;
  font-weight: bold;
`;

const StyledOffcanvasBody = styled.div`
  overflow-y: auto;
  padding: 16px;
  /* background-color: ${({ theme }) => theme.colors.fundo}; */
`;

const StyledOffcanvasFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0 16px;
  border-top: 1px solid #ddd;
  background: #f8f9fa;
`;

interface OffcanvasProps {
  placement: OffcanvasPlacement;
  name: string;
  show: boolean;
  onHide: () => void;
  style?: CSSProperties;
  children?: React.ReactNode;
  currentUrl?: string;
  selectedRowId?: number;
  displayValues?: Record<string, string>;
  selectedRow?: Record<string, any>;
  tituloCanvas?: string;
  botaoAbrir?: boolean;
  tipoCopiar?: string;
  mostrarBotaoCopiar?: boolean;
  path?: string;
  onAbrirClick?: (selectedRow: any) => void;
}

const OffCanvas: React.FC<OffcanvasProps> = ({
  placement,
  name,
  show,
  onHide,
  style,
  children,
  currentUrl,
  selectedRowId,
  displayValues,
  selectedRow,
  tituloCanvas,
  botaoAbrir = true,
  tipoCopiar = 'default',
  mostrarBotaoCopiar = true,
  path,
  onAbrirClick,
}) => {
  const navigate = useNavigate();
  const [iconName, setIconName] = useState('content_copy'); // Estado do ícone

  const redirecionaPorId = () => {
    if (onAbrirClick && selectedRow) {
      onAbrirClick(selectedRow);
      return;
    }
    if (selectedRowId) {
      const fichaPath = `/ficha/${selectedRowId}/dadosgerais`;

      if (path) {
        // Ajusta a URL para usar HashRouter corretamente
        const basePath = path.startsWith('/') ? path : `/${path}`;
        const url = `${window.location.origin}/#${basePath}${fichaPath}`;
        window.open(url, '_blank');
      } else {
        // Navegação dentro do SPA (React Router)
        navigate(`${currentUrl}${fichaPath}`);
      }
    }
  };

  const copiarDados = () => {
    if (!selectedRow || !displayValues) {
      setIconName('error'); // Mostrar um ícone de erro
      setTimeout(() => setIconName('content_copy'), 2000);
      return;
    }

    // Adiciona o título no início, se existir
    const linhas = tituloCanvas ? [`*${tituloCanvas}*`] : [];

    // Adiciona os dados do selectedRow
    linhas.push(
      ...Object.entries(selectedRow)
        .filter(
          ([key, value]) =>
            value != null &&
            (displayValues[key] || key.startsWith('separador')),
        ) // Inclui separadores
        .map(([key, value]) => {
          if (key.startsWith('separador')) {
            return ''; // Linha vazia para separadores
          }

          let valorFormatado;

          if (React.isValidElement(value)) {
            // Converte o elemento React para HTML e extrai o texto
            const staticMarkup = ReactDOMServer.renderToStaticMarkup(value);
            const parser = new DOMParser();
            const parsedHtml = parser.parseFromString(
              staticMarkup,
              'text/html',
            );

            // Caso especial: bullet points
            if (parsedHtml.body.querySelector('ul')) {
              const listItems = Array.from(
                parsedHtml.body.querySelectorAll('li'),
              )
                .map((li) => `- ${li.textContent?.trim()}`)
                .join('\n');
              valorFormatado = `\n${listItems}`;
            } else {
              valorFormatado = parsedHtml.body.textContent || '';
            }
          } else if (
            typeof value === 'object' &&
            value !== null &&
            'props' in value
          ) {
            // Para objetos com 'props', extrai texto do 'props.texto'
            valorFormatado = (value.props.texto as string | undefined) ?? '';
          } else {
            valorFormatado = String(value); // Converte para string
          }

          return `*${displayValues[key]}:* ${valorFormatado}`;
        }),
    );

    // Junta as linhas com quebras de linha
    const textoFormatado = linhas.join('\n');

    // Copia para a área de transferência
    navigator.clipboard
      .writeText(textoFormatado)
      .then(() => {
        setIconName('check_circle'); // Muda o ícone para 'check_circle'
        setTimeout(() => setIconName('content_copy'), 2000); // Volta para 'content_copy' após 2 segundos
      })
      .catch((err) => {
        console.error('Erro ao copiar os dados para o clipboard:', err);
        setIconName('error'); // Mostrar ícone de erro
        setTimeout(() => setIconName('content_copy'), 2000);
      });
  };

  const copiarDadosSimples = () => {
    if (!children) {
      setIconName('error');
      setTimeout(() => setIconName('content_copy'), 2000);
      return;
    }

    try {
      // Cria um contêiner DOM real para renderizar os children
      const tempDiv = document.createElement('div');
      document.body.appendChild(tempDiv); // Adiciona temporariamente ao DOM

      // Renderiza os children no contêiner (compatível com React 17 ou anterior)
      ReactDOM.render(<>{children}</>, tempDiv);

      // Extrai o texto do contêiner
      const texto = Array.from(tempDiv.childNodes)
        .map((node) => {
          if (node.nodeName === 'P') {
            return node.textContent?.trim() || ''; // Pega o texto de <p> com quebra de linha
          } else if (node.nodeName === 'BR') {
            return '\n'; // Adiciona uma quebra de linha para <br>
          } else if (node.nodeName === 'UL') {
            // Processa listas (<ul> e <li>)
            return Array.from(node.childNodes)
              .map((liNode) =>
                liNode.textContent ? `- ${liNode.textContent.trim()}` : '',
              )
              .join('\n');
          } else {
            return node.textContent?.trim() || ''; // Qualquer outro elemento
          }
        })
        .filter((line) => line.length > 0) // Remove linhas vazias
        .join('\n'); // Junta com quebras de linha

      // Remove o contêiner temporário
      document.body.removeChild(tempDiv);

      // Copia o texto formatado para o clipboard
      navigator.clipboard
        .writeText(texto)
        .then(() => {
          setIconName('check_circle');
          setTimeout(() => setIconName('content_copy'), 2000);
        })
        .catch((err) => {
          console.error(
            'Erro ao copiar os dados simples para o clipboard:',
            err,
          );
          setIconName('error');
          setTimeout(() => setIconName('content_copy'), 2000);
        });
    } catch (error) {
      console.error('Erro ao processar os children para copiar:', error);
      setIconName('error');
      setTimeout(() => setIconName('content_copy'), 2000);
    }
  };

  const handleCopiar = () => {
    if (tipoCopiar === 'simples') {
      copiarDadosSimples();
    } else {
      copiarDados();
    }
  };

  return (
    <Offcanvas show={show} onHide={onHide} placement={placement} style={style}>
      <StyledOffcanvas>
        <StyledOffcanvasHeader>
          <span>{name}</span>
          {mostrarBotaoCopiar && (
            <span
              className="material-icons"
              style={{ cursor: 'pointer' }}
              onClick={handleCopiar}
            >
              {iconName}
            </span>
          )}
        </StyledOffcanvasHeader>
        <StyledOffcanvasBody>{children}</StyledOffcanvasBody>
        <StyledOffcanvasFooter>
          <div className="d-flex justify-content-end align-items-center">
            {botaoAbrir && (
              <WorkspaceBotoes open onClickOpen={redirecionaPorId} />
            )}
          </div>
        </StyledOffcanvasFooter>
      </StyledOffcanvas>
    </Offcanvas>
  );
};

export default OffCanvas;
